import { Location } from '@angular/common';
import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { WindowRef } from '@spartacus/core';
import { filter, map, Observable, pairwise, ReplaySubject, startWith, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NavigationSourceService {
  private storageKey = 'sdNavigationSource';
  private previousUrl$ = new ReplaySubject<string | undefined>(1);

  constructor(
    private router: Router,
    private destroyRef: DestroyRef,
    private windowRef: WindowRef,
    private location: Location,
  ) {}

  init(): void {
    if (this.windowRef.isBrowser()) {
      const prevUrlFromStorage = this.getFromStorage();

      this.router.events
        .pipe(
          filter(
            (event): event is NavigationStart =>
              event instanceof NavigationStart && event.navigationTrigger !== 'popstate',
          ),
          map((event) => event.url),
          startWith(prevUrlFromStorage),
          pairwise(),
          map(([prevUrl]) => prevUrl),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe((prevUrl) => {
          this.previousUrl$.next(prevUrl);

          if (prevUrl) {
            this.syncWithStorage(prevUrl);
          }
        });
    }
  }

  getNavigationSource(): Observable<string | undefined> {
    return this.previousUrl$.asObservable();
  }

  goBack(): void {
    this.getNavigationSource()
      .pipe(take(1))
      .subscribe((source) => {
        if (source === undefined) {
          this.router.navigateByUrl('/');
        } else {
          this.location.back();
        }
      });
  }

  private getFromStorage(): string | undefined {
    return this.windowRef.sessionStorage?.getItem(this.storageKey) ?? undefined;
  }

  private syncWithStorage(value: string): void {
    this.windowRef.sessionStorage?.setItem(this.storageKey, value);
  }
}
